import React from 'react';
import { PackagePreview } from '../../types';
import { ConfirmationModal, ConfirmationType } from '../shared/ui';

interface LenderPackageSelectStepProps {
  packages: PackagePreview[];
  selectedKeys: Set<string>;
  selectedExistingPackages: PackagePreview[];
  isLoading: boolean;
  isReplaceWarningOpen: boolean;
  onBack: () => void;
  onToggleKey: (key: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onRequestImport: () => void;
  onConfirmReplace: () => void;
  onCloseReplaceWarning: () => void;
}

/**
 * Presentational step that lists packages parsed from a CSV, lets the user pick
 * which to import, flags packages that already exist, and gates the import behind
 * a danger confirmation when any selected package would be replaced.
 */
export const LenderPackageSelectStep: React.FC<LenderPackageSelectStepProps> = ({
  packages,
  selectedKeys,
  selectedExistingPackages,
  isLoading,
  isReplaceWarningOpen,
  onBack,
  onToggleKey,
  onSelectAll,
  onDeselectAll,
  onRequestImport,
  onConfirmReplace,
  onCloseReplaceWarning,
}) => {
  const replaceCount = selectedExistingPackages.length;
  const hasReplacements = replaceCount > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-700 transition-colors">
          &larr; Back
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">{selectedKeys.size} of {packages.length} selected</span>
          <button onClick={onSelectAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Select All</button>
          <span className="text-xs text-slate-300">|</span>
          <button onClick={onDeselectAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Deselect All</button>
        </div>
      </div>

      <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-slate-200">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
              <th className="w-10 px-4 py-2.5" />
              <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Package Name</th>
              <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right w-28">Items</th>
              <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right w-28">BOM Items</th>
            </tr>
          </thead>
          <tbody>
            {packages.map((pkg, idx) => (
              <tr
                key={pkg.package_key}
                onClick={() => onToggleKey(pkg.package_key)}
                className={`cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors ${
                  selectedKeys.has(pkg.package_key) ? 'bg-blue-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'
                } hover:bg-blue-50/70`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedKeys.has(pkg.package_key)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleKey(pkg.package_key)}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-xs font-medium text-slate-800">
                  <span className="inline-flex items-center gap-2">
                    {pkg.package_name}
                    {pkg.already_exists && (
                      <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-red-200 bg-red-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-red-600">
                        Exists · will be replaced
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 text-right tabular-nums">{pkg.item_count}</td>
                <td className="px-4 py-3 text-sm text-slate-600 text-right tabular-nums">{pkg.bom_item_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasReplacements && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700">
          <span className="font-semibold">{replaceCount}</span> selected{' '}
          {replaceCount === 1 ? 'package' : 'packages'} already {replaceCount === 1 ? 'exists' : 'exist'} for this lender and will be removed and replaced on import.
        </div>
      )}

      <button
        onClick={onRequestImport}
        disabled={selectedKeys.size === 0 || isLoading}
        className={`mt-4 w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          hasReplacements ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'
        }`}
      >
        {isLoading
          ? 'Importing...'
          : hasReplacements
            ? `Replace & Import (${selectedKeys.size})`
            : `Import Selected (${selectedKeys.size})`}
      </button>

      <ConfirmationModal
        isOpen={isReplaceWarningOpen}
        type={ConfirmationType.DANGER}
        title="Replace existing packages?"
        confirmButtonText={`Replace & Import (${selectedKeys.size})`}
        cancelButtonText="Cancel"
        onClose={onCloseReplaceWarning}
        onConfirm={onConfirmReplace}
        message={
          <div className="space-y-2">
            <p>
              The following {replaceCount === 1 ? 'package' : `${replaceCount} packages`} already{' '}
              {replaceCount === 1 ? 'exists' : 'exist'} for this lender. Importing will{' '}
              <span className="font-semibold text-red-600">permanently remove the existing data</span> and replace it with the uploaded version.
            </p>
            <ul className="max-h-40 overflow-y-auto rounded-md border border-red-100 bg-red-50/60 px-3 py-2 text-xs text-slate-700">
              {selectedExistingPackages.map((pkg) => (
                <li key={pkg.package_key} className="list-disc list-inside">{pkg.package_name}</li>
              ))}
            </ul>
          </div>
        }
      />
    </div>
  );
};
